/**
 * Program Detail Screen - Shows full details about an active program
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Spinner } from 'reactstrap';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useLanguage } from '../../Hooks/useLanguage';
import { Theme } from '../../Theme/Theme';
import { CoachProgram, CoachProgramApi } from '../../Model/Coach/Program/CoachProgram';
import { MindfulnessProgram, MindfulnessProgramApi } from '../../Model/Coach/Mindfulness/MindfulnessProgram';
import { FitnessProgramTemplate, FitnessProgramTemplateApi } from '../../Model/Coach/Program/Template/FitnessProgramTemplate';
import dayjs from '../../Utils/dayjs';
import { getMediaMedium } from '../../Model/Media/Media';

const PageContainer = styled.div`
  min-height: calc(100vh - 64px);
  background: linear-gradient(180deg, ${Theme.Colors.primary}08 0%, ${Theme.Colors.background} 300px);
  padding: 32px 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${Theme.Colors.charcoal};
  font-size: 14px;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 16px;
  transition: color 0.2s;

  &:hover {
    color: ${Theme.Colors.primary};
  }

  i {
    font-size: 18px;
  }
`;

const HeroSection = styled.div<{ $image?: string }>`
  background: ${(props) => (props.$image ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${props.$image})` : Theme.Colors.primary)};
  background-size: cover;
  background-position: center;
  border-radius: 20px;
  padding: 48px 32px;
  color: white;
  margin-bottom: 24px;
`;

const ProgramType = styled.span<{ $type: 'fitness' | 'mindfulness' }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: ${(props) => (props.$type === 'mindfulness' ? '#8B5CF6' : 'rgba(255,255,255,0.2)')};
  padding: 6px 12px;
  border-radius: 20px;
  margin-bottom: 16px;
`;

const ProgramTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ProgramDescription = styled.p`
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  max-width: 600px;
  line-height: 1.6;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
`;

const StatIcon = styled.div`
  font-size: 24px;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.charcoal};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SectionIcon = styled.span`
  font-size: 20px;
`;

const ProgressContainer = styled.div`
  margin-bottom: 24px;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ProgressLabel = styled.span`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
`;

const ProgressPercent = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.primary};
`;

const ProgressBar = styled.div`
  height: 12px;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 6px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $percent: number; $type: 'fitness' | 'mindfulness' }>`
  height: 100%;
  width: ${(props) => props.$percent}%;
  background: ${(props) => (props.$type === 'mindfulness' ? '#8B5CF6' : Theme.Colors.primary)};
  border-radius: 6px;
  transition: width 0.5s ease;
`;

const DateRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid ${Theme.Colors.lightestGrey};

  &:last-child {
    border-bottom: none;
  }
`;

const DateLabel = styled.span`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
`;

const DateValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${Theme.Colors.textColor};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  color: ${Theme.Colors.charcoal};
`;

const NotFoundContainer = styled.div`
  text-align: center;
  padding: 80px 24px;
`;

const NotFoundIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const NotFoundTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const NotFoundText = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

type ProgramData = {
  id: string;
  type: 'fitness' | 'mindfulness';
  name: string;
  description?: string;
  image?: string;
  startDate?: Date;
  endDate?: Date;
  progress: number;
  totalWeeks: number;
  currentWeek: number;
  daysCompleted: number;
  totalDays: number;
};

export const ProgramDetailScreen: React.FC = observer(() => {
  const { programId, programType } = useParams<{ programId: string; programType: string }>();
  const navigate = useNavigate();
  const { t } = useTrans();
  const { language } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [programData, setProgramData] = useState<ProgramData | null>(null);

  useEffect(() => {
    const fetchProgram = async () => {
      setLoading(true);
      try {
        if (programType === 'fitness') {
          const programs = await CoachProgramApi.currentlyActive();
          const program = programs.find((p) => p.id === programId);
          if (program) {
            // Fetch the template explicitly to get full data
            const template = program.superMacroTemplateId
              ? await FitnessProgramTemplateApi.findOne(program.superMacroTemplateId).catch(() => undefined)
              : undefined;

            const templateName = template
              ? (template.name.find((n) => n.lang === language)?.value ||
                 template.name[0]?.value ||
                 'Fitness Program')
              : 'Fitness Program';
            const templateDesc = template
              ? (template.description.find((d) => d.lang === language)?.value ||
                 template.description[0]?.value)
              : undefined;
            const templateImage = template?.image ? getMediaMedium(template.image) : template?.medias?.[0] ? getMediaMedium(template.medias[0]) : undefined;

            const startDate = program.startDateTime;
            const endDate = program.endDateTime;
            const totalDuration = endDate && startDate ? endDate.getTime() - startDate.getTime() : 0;
            const elapsed = startDate ? Date.now() - startDate.getTime() : 0;
            const progress = totalDuration > 0 ? Math.min(100, Math.max(0, Math.round((elapsed / totalDuration) * 100))) : 0;
            const totalWeeks = totalDuration > 0 ? Math.ceil(totalDuration / (7 * 24 * 60 * 60 * 1000)) : 0;
            const currentWeek = totalDuration > 0 ? Math.min(totalWeeks, Math.ceil(elapsed / (7 * 24 * 60 * 60 * 1000))) : 0;
            const totalDays = totalDuration > 0 ? Math.ceil(totalDuration / (24 * 60 * 60 * 1000)) : 0;
            const daysCompleted = totalDuration > 0 ? Math.min(totalDays, Math.ceil(elapsed / (24 * 60 * 60 * 1000))) : 0;

            setProgramData({
              id: program.id,
              type: 'fitness',
              name: templateName,
              description: templateDesc,
              image: templateImage,
              startDate,
              endDate,
              progress,
              totalWeeks,
              currentWeek,
              daysCompleted,
              totalDays,
            });
          }
        } else if (programType === 'mindfulness') {
          const programs = await MindfulnessProgramApi.currentlyActive();
          const program = programs.find((p) => p.id === programId);
          if (program) {
            const template = program.template;
            const templateName = template?.getName?.(language) || 'Mindfulness Program';
            const templateDesc = template?.getDescription?.(language);
            const templateImage = template?.images?.[0] ? getMediaMedium(template.images[0]) : undefined;

            const startDate = program.startDateTime;
            const endDate = program.endDateTime;
            const totalDuration = endDate && startDate ? endDate.getTime() - startDate.getTime() : 0;
            const elapsed = startDate ? Date.now() - startDate.getTime() : 0;
            const progress = totalDuration > 0 ? Math.min(100, Math.max(0, Math.round((elapsed / totalDuration) * 100))) : 0;
            const totalWeeks = totalDuration > 0 ? Math.ceil(totalDuration / (7 * 24 * 60 * 60 * 1000)) : 0;
            const currentWeek = totalDuration > 0 ? Math.min(totalWeeks, Math.ceil(elapsed / (7 * 24 * 60 * 60 * 1000))) : 0;
            const totalDays = totalDuration > 0 ? Math.ceil(totalDuration / (24 * 60 * 60 * 1000)) : 0;
            const daysCompleted = totalDuration > 0 ? Math.min(totalDays, Math.ceil(elapsed / (24 * 60 * 60 * 1000))) : 0;

            setProgramData({
              id: program.id,
              type: 'mindfulness',
              name: templateName,
              description: templateDesc,
              image: templateImage,
              startDate,
              endDate,
              progress,
              totalWeeks,
              currentWeek,
              daysCompleted,
              totalDays,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching program:', error);
      } finally {
        setLoading(false);
      }
    };

    if (programId && programType) {
      fetchProgram();
    }
  }, [programId, programType, language]);

  const handleBack = () => {
    navigate('/account#programs');
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <LoadingContainer>
            <Spinner color="primary" />
            <p style={{ marginTop: 16 }}>{t('common.loading', { defaultValue: 'Loading...' })}</p>
          </LoadingContainer>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (!programData) {
    return (
      <PageContainer>
        <ContentWrapper>
          <BackButton onClick={handleBack}>
            <i className="bi bi-arrow-left" />
            {t('common.back', { defaultValue: 'Back' })}
          </BackButton>
          <NotFoundContainer>
            <NotFoundIcon>🔍</NotFoundIcon>
            <NotFoundTitle>{t('program.notFound.title', { defaultValue: 'Program Not Found' })}</NotFoundTitle>
            <NotFoundText>
              {t('program.notFound.description', { defaultValue: 'This program could not be found or is no longer active.' })}
            </NotFoundText>
          </NotFoundContainer>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const daysRemaining = programData.totalDays - programData.daysCompleted;

  return (
    <PageContainer>
      <ContentWrapper>
        <BackButton onClick={handleBack}>
          <i className="bi bi-arrow-left" />
          {t('common.back', { defaultValue: 'Back' })}
        </BackButton>

        <HeroSection $image={programData.image}>
          <ProgramType $type={programData.type}>
            {programData.type === 'mindfulness'
              ? t('program.type.mindfulness', { defaultValue: 'Mindfulness' })
              : t('program.type.fitness', { defaultValue: 'Fitness' })}
          </ProgramType>
          <ProgramTitle>{programData.name}</ProgramTitle>
          {programData.description && <ProgramDescription>{programData.description}</ProgramDescription>}
        </HeroSection>

        <StatsGrid>
          <StatCard>
            <StatIcon>📅</StatIcon>
            <StatValue>{programData.currentWeek}</StatValue>
            <StatLabel>{t('program.stat.currentWeek', { defaultValue: 'Current Week' })}</StatLabel>
          </StatCard>
          <StatCard>
            <StatIcon>🎯</StatIcon>
            <StatValue>{programData.totalWeeks}</StatValue>
            <StatLabel>{t('program.stat.totalWeeks', { defaultValue: 'Total Weeks' })}</StatLabel>
          </StatCard>
          <StatCard>
            <StatIcon>✅</StatIcon>
            <StatValue>{programData.daysCompleted}</StatValue>
            <StatLabel>{t('program.stat.daysCompleted', { defaultValue: 'Days In' })}</StatLabel>
          </StatCard>
          <StatCard>
            <StatIcon>⏳</StatIcon>
            <StatValue>{daysRemaining > 0 ? daysRemaining : 0}</StatValue>
            <StatLabel>{t('program.stat.daysRemaining', { defaultValue: 'Days Left' })}</StatLabel>
          </StatCard>
        </StatsGrid>

        <Section>
          <SectionTitle>
            <SectionIcon>📊</SectionIcon>
            {t('program.section.progress', { defaultValue: 'Progress' })}
          </SectionTitle>
          <ProgressContainer>
            <ProgressHeader>
              <ProgressLabel>
                {t('program.progress.weekOf', {
                  defaultValue: 'Week {{current}} of {{total}}',
                  current: programData.currentWeek,
                  total: programData.totalWeeks,
                })}
              </ProgressLabel>
              <ProgressPercent>{programData.progress}%</ProgressPercent>
            </ProgressHeader>
            <ProgressBar>
              <ProgressFill $percent={programData.progress} $type={programData.type} />
            </ProgressBar>
          </ProgressContainer>
        </Section>

        <Section>
          <SectionTitle>
            <SectionIcon>📆</SectionIcon>
            {t('program.section.schedule', { defaultValue: 'Schedule' })}
          </SectionTitle>
          <DateRow>
            <DateLabel>{t('program.date.started', { defaultValue: 'Started' })}</DateLabel>
            <DateValue>
              {programData.startDate ? dayjs(programData.startDate).format('MMMM D, YYYY') : '-'}
            </DateValue>
          </DateRow>
          <DateRow>
            <DateLabel>{t('program.date.ends', { defaultValue: 'Ends' })}</DateLabel>
            <DateValue>
              {programData.endDate ? dayjs(programData.endDate).format('MMMM D, YYYY') : '-'}
            </DateValue>
          </DateRow>
          <DateRow>
            <DateLabel>{t('program.date.duration', { defaultValue: 'Duration' })}</DateLabel>
            <DateValue>
              {programData.totalWeeks} {t('program.weeks', { defaultValue: 'weeks' })}
            </DateValue>
          </DateRow>
        </Section>
      </ContentWrapper>
    </PageContainer>
  );
});
